import { CSSProperties } from "@mui/material/styles/createTypography";
import { styled } from "@mui/system";
import { black, blue, grey } from "designSystem/styles/color";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";

export const ContentContainer = styled("div")(({
	theme,
}) => ({
	lineHeight: "normal",
	position: "relative",
	width: "100%",
	overflowWrap: "break-word",
	"& at": {
		color: "#5b5fc7",
	},
	"& .fr-view blockquote": {
		backgroundColor: "#f5f5f5",
		color: "#424242",
		margin: "8px 0",
		padding: "8px 16px 8px 12px",
		borderLeft: "4px solid #d1d1d1",
	},
	"& .fr-view pre": {
		backgroundColor: "#f5f5f5",
		margin: "8px 0",
		padding: "8px 16px 8px",
	},
	"& .fr-view table": {
		boxSizing: "border-box",
	},
}));

export const MessageGroupTitleContainer = styled("div")(({
	theme,
}) => ({
	alignSelf: "center",
	backgroundColor: "#FFFFFF",
	border: STANDARD_BORDER_STYLE,
	borderRadius: "13px",
	boxShadow: "0px 1px 3px 0px rgba(0,0,0,0.10)",
	fontSize: "12px",
	fontWeight: "bold",
	lineHeight: "15px",
	marginBottom: theme.spacing(3),
	padding: "5px 10px",
	position: "sticky",
	top: theme.spacing(2),
	width: "fit-content",
	zIndex: 1,
}));

export function useChannelNameStyles(): Record<string, CSSProperties> {
	return {
		nameIcon: {
			height: 16,
			left: 5,
			position: "absolute",
			top: 4,
			width: 16,
		},
		nameInput: {
			paddingLeft: 6,
		},
	};
}

export function useMessageStyles(theme): Record<string, CSSProperties> {
	return {
		attachment: {
			paddingTop: theme.spacing(1),
			width: "100%",
		},
		avatar: {
			borderRadius: 2,
			marginRight: theme.spacing(2),
			height: 32,
			width: 32,
		},
		file: {
			paddingTop: theme.spacing(1),
			width: "100%",
		},
		header: {
			width: "100%",
		},
		importantText: {
			color: "#c4314b",
			fontSize: 14,
			fontWeight: 700,
			textTransform: "uppercase",
		},
		message: {
			flex: 1,
			flexWrap: "nowrap",
			fontSize: 12,
			fontWeight: 400,
			lineHeight: "14px",
			marginBottom: theme.spacing(0.8),
			minWidth: 0,
			width: "100%",
			height: "100%",
		},
		messageContainer: {
			position: "relative",
			flexWrap: "nowrap",
			flexShrink: 0,
			justifyItems: "start",
			width: "100%",
			paddingBottom: theme.spacing(3),
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
		},
		messageRepliesLink: {
			padding: 0,
		},
		reaction: {
			padding: "4px 6px",
			marginTop: 4,
			marginRight: 4,
			borderRadius: 12,
			backgroundColor: grey[100],
		},
		reactions: {
			position: "absolute",
			right: 0,
			top: 0,
		},
		reactionsContainer: {
			display: "flex",
			flexWrap: "wrap",
			flexGrow: 1,
		},
		replyAvatar: {
			borderRadius: 2,
			marginRight: theme.spacing(2),
			height: 24,
			width: 24,
		},
		subject: {
			fontSize: 16,
			fontWeight: 700,
			lineHeight: "20px",
		},
		timestamp: {
			color: grey[900],
		},
		unsupported: {
			backgroundColor: grey[100],
			borderRadius: 3,
			padding: theme.spacing(2),
		},
		username: {
			fontWeight: 700,
			paddingRight: theme.spacing(1),
		},
		warning: {
			width: "100%",
			overflowWrap: "break-word",
		},
	};
}

export function useMessageGroupStyles(theme): Record<string, CSSProperties> {
	return {
		container: {
			flexShrink: 0,
			flexWrap: "nowrap",
			width: "100%",
			"&:last-child": {
				paddingTop: theme.spacing(2),
			},
		},
		divider: {
			backgroundColor: grey[400],
			position: "relative",
			width: "100%",
			top: "18px",
		},
		messages: {
			flexDirection: "column-reverse",
			flexShrink: 0,
			flexWrap: "nowrap",
			width: "100%",
		},
	};
}

export function useConversationHistoryStyles(theme): Record<string, CSSProperties> {
	return {
		messageSkeleton: {
			gap: 2,
			margin: 3,
			width: "auto",
		},
	};
}

export function useAttachmentCardStyles(theme): Record<string, CSSProperties> {
	return {
		attachment: {
			flexWrap: "nowrap",
			width: "100%",
		},
		attachmentBar: {
			flexShrink: 0,
			borderColor: blue[600],
			border: "solid 2px",
			borderRadius: "2px",
			marginRight: theme.spacing(3),
			width: "4px",
		},
		attachmentContainer: {
			flexWrap: "nowrap",
			gap: 4,
		},
		attachmentsContainer: {
			border: "1px solid",
			borderColor: grey[400],
			borderRadius: 3,
			margin: "4px 0",
			padding: 4,
		},
		attachmentContent: {
			width: "100%",
			flexShrink: 1,
			paddingBottom: theme.spacing(1),
			paddingTop: theme.spacing(1),
			"& > div:first-child": {
				fontWeight: "bold",
			},
		},
		codeSnippetText: {
			whiteSpace: "pre",
		},
	};
}

export function useChannelListStyles(theme): Record<string, CSSProperties> {
	return {
		root: {
			boxSizing: "border-box",
			marginBottom: theme.spacing(3),
			width: "100%",
		},
		emptyRoot: {
			paddingLeft: theme.spacing(3),
			paddingRight: theme.spacing(3),
			paddingBottom: theme.spacing(3),
			width: "100%",
		},
		emptyInside: {
			backgroundColor: grey[100],
			borderRadius: "3px",
			color: black[100],
			fontSize: "12px",
			lineHeight: "14px",
			padding: theme.spacing(2),
			width: "100%",
		},
		subtitleContainer: {
			marginBottom: 2,
		},
		titleContainer: {
			padding: "12px 12px 8px 12px",
		},
	};
};